import { useCallback, useEffect, useRef, useState } from "react";
import { transcribeAudio } from "@/lib/api";

const SILENCE_MS = 1300;
const MAX_UTTERANCE_MS = 20000;
const MIN_SPEECH_MS = 300;
const RMS_THRESHOLD = 0.025;

// Hands-free voice: continuously listens while `active`, detects an utterance via
// simple voice-activity detection, transcribes it and calls onUtterance(text).
export function useHandsFree({ active, onUtterance, onSpeechStart, onError }) {
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  const streamRef = useRef(null);
  const recRef = useRef(null);
  const ctxRef = useRef(null);
  const rafRef = useRef(null);
  const chunksRef = useRef([]);
  const speechStartRef = useRef(0);
  const lastVoiceRef = useRef(0);
  const cbRef = useRef({ onUtterance, onSpeechStart, onError });
  useEffect(() => { cbRef.current = { onUtterance, onSpeechStart, onError }; });

  const cleanup = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    try { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); } catch (e) {}
    recRef.current = null;
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (ctxRef.current) { ctxRef.current.close().catch(() => {}); ctxRef.current = null; }
    setListening(false);
  }, []);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      const data = new Uint8Array(analyser.fftSize);

      const rec = new MediaRecorder(stream);
      recRef.current = rec;
      chunksRef.current = [];
      speechStartRef.current = 0;
      lastVoiceRef.current = 0;
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        const hadSpeech = speechStartRef.current > 0;
        const dur = hadSpeech ? performance.now() - speechStartRef.current : 0;
        cleanup();
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (!hadSpeech || dur < MIN_SPEECH_MS || blob.size < 1200) return;
        setTranscribing(true);
        try {
          const text = await transcribeAudio(blob);
          if (text && text.trim()) cbRef.current.onUtterance(text.trim());
        } catch (e) {
          cbRef.current.onError && cbRef.current.onError(e);
        } finally {
          setTranscribing(false);
        }
      };
      rec.start(100);
      setListening(true);

      const tick = () => {
        if (!recRef.current) return;
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / data.length);
        const now = performance.now();
        if (rms > RMS_THRESHOLD) {
          if (!speechStartRef.current) { speechStartRef.current = now; cbRef.current.onSpeechStart && cbRef.current.onSpeechStart(); }
          lastVoiceRef.current = now;
        }
        const started = speechStartRef.current > 0;
        if (started && (now - lastVoiceRef.current > SILENCE_MS || now - speechStartRef.current > MAX_UTTERANCE_MS)) {
          try { if (recRef.current.state !== "inactive") recRef.current.stop(); } catch (e) {}
          return;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      cbRef.current.onError && cbRef.current.onError(e);
      cleanup();
    }
  }, [cleanup]);

  useEffect(() => {
    if (active) start();
    else cleanup();
    return () => cleanup();
  }, [active, start, cleanup]);

  return { listening, transcribing };
}
